import React, { useEffect, useRef, useState } from "react";
import pdfMake from "pdfmake/build/pdfmake";
import pdfFonts from "pdfmake/build/vfs_fonts";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Dialog } from "primereact/dialog";
import { Toast } from "primereact/toast";
import { ButtonInput } from "../../components/ButtonInput/ButtonInput";
import { TextInput } from "../../components/TextInput/TextInput";
import { DropdownInput } from "../../components/DropdownInput/DropdownInput";
import { DateInput } from "../../components/DateInput/DateInput";
import { RadioButtonInput } from "../../components/RadioButtonInput/RadioButtonInput";
import { TextInputWithIcon } from "../../components/TextInputWithIcon/TextInputWithIcon";
import { TextAreaInput } from "../../components/TextAreaInput/TextAreaInput";
import { getAllProducts } from "../../services/ProductService";
import { getAllPartners } from "../../services/PartnerService";
import { getAllWarehouses } from "../../services/WarehouseService";
import { getAllCities, createCity } from "../../services/CityService";
import { Product, ProductType } from "../../models/Product";
import { Warehouse } from "../../models/Warehouse";
import { Partner } from "../../models/Partner";
import { City } from "../../models/City";
import {
  ProcessingRequestCreate,
  ProcessingRequestUpdate,
  ProcessingRequestDetails,
} from "../../models/ProcessingRequest";
import { Utility } from "../../core/Utility";
import {
  addNewProcessingRequest,
  updateOneProcessingRequest,
  getNextProcessingRequestNumber,
  createOwnershipDocumentForRequest,
  transferBadQualityForRequest,
} from "../../services/FinishingCenterService";
import { getDenaturationRecordTemplate } from "../../document-templates/DenaturationRecordTemplate";
import "./ProcessingRequestForm.scss";

(pdfMake as any).vfs = pdfFonts.pdfMake.vfs;

export interface ProcessingRequestFormProps {
  show?: string;
  request?: ProcessingRequestDetails;
  buttonLabel?: string;
  buttonIcon?: string;
  buttonStyle?: React.CSSProperties;
  buttonSaveStyle?: React.CSSProperties;
  buttonResetStyle?: React.CSSProperties;
  refreshingData?: () => void;
}

type ApprovalRow = {
  id?: string;
  number: string;
  date?: Date | null;
};

type FormState = {
  id?: string;
  number: string;
  date?: Date | null;
  productId?: string;
  partnerId?: string;
  originPlace?: string;
  address?: string;
  amountKg?: number | null;
  harvestYear?: number | null;
  transportCostRwa?: boolean;
  note?: string;
  qualityOk?: boolean;
  denaturationBasis?: string;
  newWarehouse?: any;
  newProduct?: any;
  approvals: ApprovalRow[];
};

export const ProcessingRequestForm: React.FC<ProcessingRequestFormProps> = (
  p,
) => {
  const {
    request,
    show,
    buttonLabel,
    buttonIcon,
    buttonStyle,
    buttonSaveStyle,
    buttonResetStyle,
    refreshingData,
  } = p;

  const { t } = useTranslation();
  const toast = useRef<Toast | null>(null);

  const [citiesList, setCitiesList] = useState<City[]>([]);
  const [productsList, setProductsList] = useState<Product[]>([]);
  const [partnersList, setPartnersList] = useState<Partner[]>([]);
  const [warehousesList, setWarehousesList] = useState<Warehouse[]>([]);
  const [showOwnershipSection, setShowOwnershipSection] = useState(false);
  const [ownershipAmount, setOwnershipAmount] = useState<number | null>(null);
  const [ownershipUsedAmountLocal, setOwnershipUsedAmountLocal] = useState<
    number | null
  >(null);

  const [visible, setVisible] = useState<boolean>(false);
  const [form, setForm] = useState<FormState>({
    id: request?.id,
    number: request?.number ?? "",
    date: request?.date ? new Date(request.date) : new Date(),
    productId: request?.productId,
    partnerId: request?.partnerId,
    originPlace: request?.originPlace ?? "",
    address: request?.address ?? "",
    amountKg: request?.amountKg ?? null,
    harvestYear: request?.harvestYear ?? new Date().getFullYear(),
    transportCostRwa: request?.transportCostRwa ?? false,
    note: request?.note ?? "",
    qualityOk: undefined,
    denaturationBasis: request?.denaturationBasis ?? "",
    newWarehouse: {} as any,
    newProduct: {} as any,
    approvals: [],
  });
  const [selectedCityId, setSelectedCityId] = useState<string | undefined>(
    undefined,
  );
  const [visibleNewCityForm, setVisibleNewCityForm] = useState<boolean>(false);
  const [newCity, setNewCity] = useState<City>({
    id: "",
    code: "",
    name: "",
  });

  const {
    control,
    formState: { errors },
    handleSubmit,
    reset,
    setValue,
  } = useForm({
    mode: "all",
  });

  const loadNextNumber = async (yearParam?: number) => {
    try {
      const year = yearParam ?? form.harvestYear ?? new Date().getFullYear();

      const res = await getNextProcessingRequestNumber(year);
      const nextNum = res.data?.result as string;

      setForm((prev) => ({
        ...prev,
        number: nextNum,
      }));
      setValue("number", nextNum);
    } catch (err) {
      console.error(err);
      // @ts-ignore
      toast.current?.show({
        severity: "error",
        detail: t("Greška pri generisanju broja zahteva"),
        life: 3000,
      });
    }
  };

  useEffect(() => {
    if (visible) {
      if (!form.id) {
        // Novi zahtev – povuci sledeći broj
        const year = form.harvestYear ?? new Date().getFullYear();
        loadNextNumber(year);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible]);

  // inicijalno učitavanje proizvoda/partnera/gradova/magacina
  useEffect(() => {
    const run = async () => {
      try {
        const [prodRes, partRes, cityRes, whRes] = await Promise.all([
          getAllProducts(),
          getAllPartners(),
          getAllCities(),
          getAllWarehouses(),
        ]);

        const prods: Product[] = prodRes.data?.result ?? prodRes.data ?? [];
        const parts: Partner[] = partRes.data?.result ?? partRes.data ?? [];
        const cits: City[] = cityRes.data?.result ?? cityRes.data ?? [];
        const whs: Warehouse[] = whRes.data?.result ?? whRes.data ?? [];

        setProductsList(prods);
        setPartnersList(parts);
        setCitiesList(cits);
        setWarehousesList(whs);
      } catch (err) {
        console.error(err);
        // @ts-ignore
        toast.current?.show({
          severity: "error",
          detail: t("Greška pri učitavanju podataka"),
          life: 3000,
        });
      }
    };

    run();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [t]);

  // ako spolja dobiješ `show` trigger
  useEffect(() => {
    if (show) {
      setVisible(true);
    }
  }, [show]);

  useEffect(() => {
    if (!request?.originPlace) {
      setSelectedCityId(undefined);
      return;
    }
    const c = citiesList.find((x) => x.name === request.originPlace);
    setSelectedCityId(c?.id);
  }, [request?.originPlace, citiesList]);

  // ako dođe novi `request` (edit mode)
  useEffect(() => {
    if (request) {
      setForm({
        id: request.id,
        number: request.number,
        date: request.date ? new Date(request.date) : new Date(),
        productId: request.productId,
        partnerId: request.partnerId,
        originPlace: request.originPlace ?? "",
        address: request.address ?? "",
        amountKg:
          request.amountKg != null
            ? Math.round(Number(request.amountKg))
            : null,
        harvestYear: request.harvestYear ?? new Date().getFullYear(),
        transportCostRwa: request.transportCostRwa ?? false,
        note: request.note ?? "",
        qualityOk: request.qualityOk,
        denaturationBasis: request.denaturationBasis ?? "",
        newWarehouse: {} as any,
        newProduct: {} as any,
        approvals: request.approvals?.length
          ? request.approvals.map((a) => ({
              id: a.id,
              number: a.number ?? "",
              date: a.date ? new Date(a.date as any) : undefined,
            }))
          : [{ number: "", date: undefined }],
      });
      reset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [request]);

  const showModal = () => {
    if (visible) {
      // Kada se dijalog ZATVARA - resetuj formu
      resetForm();
    } else {
      // Kada se dijalog OTVARA - ako je novi zahtev, učitaj sledeći broj
      if (!request) {
        const year = form.harvestYear ?? new Date().getFullYear();
        loadNextNumber(year);
      }
    }
    setVisible((prev) => !prev);
  };

  const handleFormChange = (prop: string, value: any) => {
    setForm((prev) => {
      const updated: FormState = { ...prev };

      if (prop === "amountKg") {
        // dozvoli brisanje
        if (value === "" || value === null || value === undefined) {
          updated.amountKg = null;
          return updated;
        }

        // radimo samo sa stringom
        const raw = String(value).replace(",", "."); // ako neko kuca zarez

        // ako NE odgovara patternu broja, ignorišemo promenu
        if (!/^\d+(\.\d+)?$/.test(raw)) {
          return prev;
        }

        const parsed = Number(raw);
        if (Number.isNaN(parsed)) {
          return prev;
        }

        updated.amountKg = parsed;
        return updated;
      } else if (prop === "harvestYear") {
        updated.harvestYear =
          value !== null && value !== "" ? Number(value) : null;
      } else if (prop === "date") {
        updated.date = value as Date;
      } else if (prop === "transportCostRwa") {
        updated.transportCostRwa = value as boolean;
      } else {
        // prop je id polja koji prosleđuju inputi (number, productId, partnerId, originPlace, address, note)
        // @ts-ignore
        updated[prop] = value;
      }
      return updated;
    });
  };

  const handleProductChange = (_: string, value: any) => {
    handleFormChange("productId", value);
  };

  const handlePartnerChange = (_: string, value: any) => {
    handleFormChange("partnerId", value);
  };

  const handleCityChange = (_: string, value: any) => {
    setSelectedCityId(value);
    const city = citiesList.find((c) => c.id === value);
    handleFormChange("originPlace", city?.name ?? "");
  };

  const handleNewCityNameChange = (_: string, value: string) => {
    setNewCity((prev) => ({ ...prev, name: value }));
  };

  const refreshCities = async () => {
    const resp = await getAllCities();
    const raw = resp?.data?.result ?? resp?.data;
    const list: City[] = Array.isArray(raw) ? raw : [];
    setCitiesList(list);
  };

  const stripEmptyId = <T extends { id?: any }>(obj: T): Omit<T, "id"> | T => {
    const copy: any = { ...obj };
    if (copy.id === "" || copy.id === null || copy.id === undefined)
      delete copy.id;
    return copy;
  };

  const addNewCity = async () => {
    try {
      if (!newCity.name.trim()) {
        // @ts-ignore
        toast.current?.show({
          severity: "error",
          detail: t("Unesite naziv mesta"),
          life: 3000,
        });
        return;
      }

      const payload: Partial<City> = stripEmptyId({
        ...newCity,
        name: newCity.name.trim(),
        code: new Date().getTime().toFixed(),
      });

      const resp = await createCity(payload);
      if (resp?.data?.status === "ERROR") {
        throw new Error(resp?.data?.message ?? "Greška");
      }

      setVisibleNewCityForm(false);
      setNewCity({ id: "", code: "", name: "" });

      await refreshCities();

      setSelectedCityId((prev) => {
        const found = citiesList.find((c) => c.name === payload.name);
        return found?.id ?? prev;
      });
      handleFormChange("originPlace", payload.name ?? "");

      // @ts-ignore
      toast.current?.show({
        severity: "success",
        detail: t("Mesto je uspešno sačuvano"),
        life: 2500,
      });
    } catch (e: any) {
      // @ts-ignore
      toast.current?.show({
        severity: "error",
        detail:
          e?.response?.data?.message ??
          e?.message ??
          t("Greška pri čuvanju mesta"),
        life: 3000,
      });
    }
  };
  const handleApprovalChange = (
    index: number,
    field: "number" | "date",
    value: any,
  ) => {
    setForm((prev) => {
      const approvals = [...(prev.approvals ?? [])];
      const current = approvals[index] ?? { number: "", date: undefined };

      if (field === "number") {
        current.number = value as string;
      } else if (field === "date") {
        current.date = value as Date;
      }

      approvals[index] = current;
      return { ...prev, approvals };
    });
  };

  const addApprovalRow = () => {
    setForm((prev) => ({
      ...prev,
      approvals: [...(prev.approvals ?? []), { number: "", date: undefined }],
    }));
  };

  const removeApprovalRow = (index: number) => {
    setForm((prev) => {
      const approvals = [...prev.approvals];
      approvals.splice(index, 1);

      // Ako smo u edit modu i obrišemo sve, ostavimo jedno prazno polje
      if (form.id && approvals.length === 0) {
        return {
          ...prev,
          approvals: [{ number: "", date: undefined }],
        };
      }

      return {
        ...prev,
        approvals,
      };
    });
  };

  const hasApprovalData = (): boolean => {
    return form.approvals.some(
      (approval) => approval.number && approval.number.trim() !== "",
    );
  };

  const handleCreateOwnershipDocument = async () => {
    try {
      if (!form.id) {
        // @ts-ignore
        toast.current?.show({
          severity: "error",
          detail: t(
            "Vlasnički list je moguće kreirati samo za već sačuvan zahtev",
          ),
          life: 3000,
        });
        return;
      }

      if (form.qualityOk !== true) {
        // @ts-ignore
        toast.current?.show({
          severity: "error",
          detail: t(
            "Vlasnički list je moguće kreirati samo ako je roba dobrog kvaliteta",
          ),
          life: 3000,
        });
        return;
      }

      setShowOwnershipSection(true);
    } catch (err: any) {
      console.error(err);
      // @ts-ignore
      toast.current?.show({
        severity: "error",
        detail: t("Greška pri prikazu sekcije za vlasnički list"),
        life: 3000,
      });
    }
  };

  const handleConfirmCreateOwnershipDocument = async () => {
    try {
      if (!form.id || !ownershipAmount || ownershipAmountInvalid) {
        return;
      }

      await createOwnershipDocumentForRequest(form.id, ownershipAmount);
      setOwnershipUsedAmountLocal(
        ownershipUsedAmount + Number(ownershipAmount),
      );

      // @ts-ignore
      toast.current?.show({
        severity: "success",
        detail: t("Vlasnički list je uspešno kreiran"),
        life: 3000,
      });

      setShowOwnershipSection(false);
      setOwnershipAmount(null);

      if (refreshingData) {
        refreshingData();
      }
    } catch (err: any) {
      console.error(err);

      const message =
        err?.response?.data?.message ||
        t("Greška pri kreiranju vlasničkog lista");

      // @ts-ignore
      toast.current?.show({
        severity: "error",
        detail: message,
        life: 4000,
      });
    }
  };

  const handleBadQualityTransfer = async () => {
    try {
      if (!form.id) {
        // @ts-ignore
        toast.current?.show({
          severity: "error",
          detail: t("Operaciju je moguće izvršiti samo nad sačuvanim zahtevom"),
          life: 3000,
        });
        return;
      }

      if (form.qualityOk !== false) {
        // @ts-ignore
        toast.current?.show({
          severity: "error",
          detail: t(
            "Ova operacija je predviđena samo kada roba nije odgovarajućeg kvaliteta",
          ),
          life: 3000,
        });
        return;
      }

      if (request?.badQualityTransferred) {
        // @ts-ignore
        toast.current?.show({
          severity: "warn",
          detail: t("Roba je već prebačena zbog lošeg kvaliteta"),
          life: 3000,
        });
        return;
      }

      if (!form.newProduct) {
        // @ts-ignore
        toast.current?.show({
          severity: "error",
          detail: t(
            "Izaberite merkantilnu robu u koju prebacujete robu lošeg kvaliteta",
          ),
          life: 3000,
        });
        return;
      }

      if (!form.newWarehouse) {
        // @ts-ignore
        toast.current?.show({
          severity: "error",
          detail: t("Izaberite magacin u koji ulazi roba lošeg kvaliteta"),
          life: 3000,
        });
        return;
      }

      await transferBadQualityForRequest(form.id, {
        newProductId: form.newProduct,
        newWarehouseId: form.newWarehouse,
      });

      // @ts-ignore
      toast.current?.show({
        severity: "success",
        detail: t("Roba lošeg kvaliteta je uspešno prebačena"),
        life: 3000,
      });

      if (refreshingData) {
        refreshingData();
      }
      if (request) {
        (request as any).badQualityTransferred = true;
      }
    } catch (err: any) {
      console.error(err);
      const message =
        err?.response?.data?.message ||
        (t("Greška pri prebacivanju robe lošeg kvaliteta") as string);

      // @ts-ignore
      toast.current?.show({
        severity: "error",
        detail: message,
        life: 4000,
      });
    }
  };

  const resetForm = () => {
    const baseYear = new Date().getFullYear();
    setForm({
      id: undefined,
      number: "",
      date: new Date(),
      productId: undefined,
      partnerId: undefined,
      originPlace: "",
      address: "",
      amountKg: null,
      harvestYear: baseYear,
      transportCostRwa: false,
      note: "",
      qualityOk: undefined,
      denaturationBasis: "",
      newWarehouse: {} as any,
      newProduct: {} as any,
      approvals: [],
    });
    setSelectedCityId(undefined);
    setVisibleNewCityForm(false);
    reset();
  };

  const onSubmit = async () => {
    try {
      if (!form.productId || !form.partnerId) {
        // @ts-ignore
        toast.current?.show({
          severity: "error",
          detail: t("Molimo popunite sva obavezna polja"),
          life: 3000,
        });
        return;
      }

      const payloadBase: ProcessingRequestCreate = {
        number: form.number,
        date: form.date ? Utility.toMiddayDate(new Date(form.date)) : undefined,
        productId: form.productId,
        partnerId: form.partnerId,
        originPlace: form.originPlace,
        address: form.address,
        amountKg: form.amountKg ?? undefined,
        harvestYear: form.harvestYear ?? undefined,
        transportCostRwa: form.transportCostRwa,
        qualityOk: form.qualityOk ?? undefined,
        denaturationBasis: form.denaturationBasis,
        note: form.note,
        approvals: (form.approvals ?? [])
          .filter((a) => a.number && a.date)
          .map((a) => ({
            id: a.id,
            number: a.number,
            date: a.date as Date,
          })),
      };

      if (form.id) {
        const updatePayload: ProcessingRequestUpdate = {
          ...payloadBase,
        };
        await updateOneProcessingRequest(form.id, updatePayload);
      } else {
        await addNewProcessingRequest(payloadBase);
      }

      // @ts-ignore
      toast.current?.show({
        severity: "success",
        detail: t("Zahtev je uspešno sačuvan"),
        life: 3000,
      });

      if (refreshingData) {
        refreshingData();
      }

      resetForm();
      showModal();
    } catch (err) {
      console.error(err);
      // @ts-ignore
      toast.current?.show({
        severity: "error",
        detail: t("Greška pri čuvanju zahteva"),
        life: 3000,
      });
    }
  };

  const onError = () => {
    // @ts-ignore
    toast.current?.show({
      severity: "error",
      detail: t("Neispravno popunjena polja"),
      life: 3000,
    });
  };

  let addButtonStyle: React.CSSProperties = {};
  if (buttonStyle) {
    addButtonStyle = buttonStyle;
  } else {
    addButtonStyle.background = "rgba(0, 165, 83, 1)";
    addButtonStyle.border = "none";
  }

  let addButtonSaveStyle: React.CSSProperties = {};
  if (buttonSaveStyle) {
    addButtonSaveStyle = buttonSaveStyle;
  } else {
    addButtonSaveStyle.background = "rgba(0, 165, 83, 1)";
    addButtonSaveStyle.border = "none";
  }

  let addButtonResetStyle: React.CSSProperties = {};
  if (buttonResetStyle) {
    addButtonResetStyle = buttonResetStyle;
  } else {
    addButtonResetStyle.background = "rgba(220, 53, 69, 1)";
    addButtonResetStyle.border = "none";
  }

  const header = (
    <div
      className="p-dialog-header-processing"
      style={{ borderBottom: "none" }}
    >
      <span className="p-dialog-titlebar-icon p-link">
        <i className="bi bi-truck" style={{ fontSize: 18 }}></i>
      </span>
      {form.id ? (
        <span>{t("Ažuriranje zahteva za prijem naturalne robe")}</span>
      ) : (
        <span>{t("Kreiranje zahteva za prijem naturalne robe")}</span>
      )}
    </div>
  );

  const footer = (
    <div className="footer-buttons">
      <ButtonInput
        labelText={
          form.id ? (t("Sačuvaj") as string) : (t("Kreiraj") as string)
        }
        onButtonClick={handleSubmit(onSubmit, onError)}
        addStyle={addButtonSaveStyle}
      />
      <ButtonInput
        labelText={t("Resetuj") as string}
        onButtonClick={resetForm}
        addStyle={addButtonResetStyle}
      />
    </div>
  );

  // filtriramo samo proizvode koji su naturalni
  const naturalProducts = productsList.filter(
    (p: Product) => p.isNatural === true,
  );
  const selectedProduct = naturalProducts.find(
    (p) => p.id === form.productId,
  ) as Product | undefined;
  const measuringUnit = selectedProduct?.measuringUnit ?? "";

  const ownershipTotalAmount = Number(request?.amountKg ?? form.amountKg ?? 0);

  const ownershipUsedAmount = Number(
    ownershipUsedAmountLocal ?? request?.ownershipUsedAmount ?? 0,
  );

  const ownershipRemainingAmount = Math.max(
    0,
    ownershipTotalAmount - ownershipUsedAmount,
  );

  const ownershipAmountInvalid =
    ownershipAmount === null ||
    ownershipAmount <= 0 ||
    ownershipAmount > ownershipRemainingAmount;

  const formatPdfDate = (value?: Date | string | null) => {
    if (!value) return "";

    const d = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(d.getTime())) return "";

    const day = `${d.getDate()}`.padStart(2, "0");
    const month = `${d.getMonth() + 1}`.padStart(2, "0");
    const year = d.getFullYear();

    return `${day}.${month}.${year}.`;
  };

  const getDenaturationFileName = () => {
    const safeNumber = (request?.number || form?.number).replace(
      /[\/\\]/g,
      "-",
    );

    return `zapisnik-o-denaturisanju-semena-${safeNumber}.pdf`;
  };

  const handleDenaturationRecord = async () => {
    try {
      if (!request?.badQualityTransferred) return;

      const amount = request?.amountKg ?? form.amountKg ?? "";
      const formattedAmount = amount !== "" ? Math.round(Number(amount)) : "";

      const pdfDefinition = getDenaturationRecordTemplate({
        requestNumber: request?.number,
        date: formatPdfDate(request?.badQualityTransferredAt || new Date()),
        productName:
          request?.productName ||
          productsList.find((p) => p.id === form.productId)?.name ||
          "" ||
          "",
        amountKg: formattedAmount,
        place: "U Rumenci",
        denaturationBasis: request?.denaturationBasis ?? form.denaturationBasis,
      });

      pdfMake.createPdf(pdfDefinition).download(getDenaturationFileName());
    } catch (err) {
      console.error(err);
      toast.current?.show({
        severity: "error",
        detail: t("Greška pri generisanju zapisnika o denaturisanju"),
        life: 3000,
      });
    }
  };

  return (
    <>
      <ButtonInput
        labelText={buttonLabel as string}
        icon={buttonIcon}
        onButtonClick={showModal}
        addStyle={addButtonStyle}
      />
      <Toast ref={toast} position="top-right" />
      {visible && (
        <Dialog
          header={header}
          visible={true}
          style={{ width: "50vw" }}
          className="new-processing-request-dialog"
          footer={footer}
          blockScroll={false}
          draggable={false}
          onHide={() => {
            resetForm();
            showModal();
          }}
        >
          <div className="new-processing-request-form-body">
            <TextInput
              id="number"
              value={form.number}
              onValueChange={handleFormChange}
              labelText={`${t("Broj zahteva")} *`}
              placeholderText={t("Unesite broj zahteva")}
              control={control}
              setValue={setValue}
              errors={errors}
              rules={{ required: true }}
            />

            <DateInput
              id="date"
              value={form.date ?? new Date()}
              onValueChange={handleFormChange}
              labelText={`${t("Datum zahteva")} *`}
              placeholderText={t("Izaberite datum")}
              icon="bi bi-calendar-check"
              control={control}
              errors={errors}
            />

            <DropdownInput
              id="productId"
              value={form.productId}
              onValueChange={handleProductChange}
              optionValue="id"
              optionLabel="name"
              options={naturalProducts}
              filter={true}
              labelText={`${t("Roba")} *`}
              placeholderText={t("Izaberite iz liste")}
              control={control}
              setValue={setValue}
              errors={errors}
              rules={{ required: true }}
            />

            <DropdownInput
              id="partnerId"
              value={form.partnerId}
              onValueChange={handlePartnerChange}
              optionValue="id"
              optionLabel="name"
              options={partnersList as Partner[]}
              filter={true}
              labelText={`${t("Vlasnik robe")} *`}
              placeholderText={t("Izaberite iz liste")}
              control={control}
              setValue={setValue}
              errors={errors}
              rules={{ required: true }}
            />

            <div className="city-content">
              <div className="city-id-content">
                <DropdownInput
                  id="originPlaceCity"
                  value={selectedCityId}
                  onValueChange={handleCityChange}
                  optionValue="id"
                  optionLabel="name"
                  options={citiesList}
                  filter={true}
                  labelText={t("Polazno mesto")}
                  placeholderText={t("Izaberite iz liste")}
                  control={control}
                  setValue={setValue}
                  errors={errors}
                  rules={{}}
                />
              </div>
              <div className="city-add-button">
                <ButtonInput
                  labelText={t("Dodaj mesto") as string}
                  onButtonClick={() => setVisibleNewCityForm((prev) => !prev)}
                  icon="bi bi-plus-square"
                  iconAlign="left"
                  addStyle={{
                    background: "rgba(4, 102, 143, 1)",
                    border: "none",
                  }}
                />
              </div>

              {visibleNewCityForm && (
                <div className="new-city-name">
                  <TextInputWithIcon
                    id="newCityName"
                    value={newCity.name}
                    onValueChange={handleNewCityNameChange}
                    labelText={t("Naziv mesta/naselja") as string}
                    icon="bi bi-signpost"
                    iconAlign="left"
                    placeholderText={t("Unesite mesto/naselje")}
                    control={control}
                    errors={errors}
                  />
                </div>
              )}

              {visibleNewCityForm && (
                <div className="new-city-add-button">
                  <ButtonInput
                    labelText={t("Dodaj") as string}
                    onButtonClick={addNewCity}
                    addStyle={{
                      background: "rgba(4, 102, 143, 1)",
                      border: "none",
                    }}
                  />
                </div>
              )}
            </div>
            <div className="place-address">
              <TextInput
                id="address"
                value={form.address ?? ""}
                onValueChange={handleFormChange}
                labelText={t("Adresa polaznog mesta")}
                placeholderText={t("Unesite adresu polaznog mesta")}
                control={control}
                setValue={setValue}
                errors={errors}
                rules={{}}
              />
            </div>

            <div className="amount-content">
              <TextInput
                id="amountKg"
                value={form.amountKg ?? ""}
                onValueChange={handleFormChange}
                labelText={`${t("Količina (kg)")} *`}
                placeholderText={t("Unesite količinu")}
                control={control}
                setValue={setValue}
                errors={errors}
                rules={{
                  required: true,
                  pattern: {
                    value: /^\d+(\.\d+)?$/,
                    message: t("Molimo unesite broj") as string,
                  },
                }}
              />

              <TextInput
                id="measureUnit"
                value={measuringUnit}
                onValueChange={() => {}}
                labelText={t("Merna jed.") as string}
                placeholderText=""
                control={control}
                setValue={setValue}
                errors={errors}
                rules={{}}
                disabled={true}
              />
            </div>

            <DropdownInput
              id="harvestYear"
              value={form.harvestYear ?? undefined}
              onValueChange={handleFormChange}
              options={[
                {
                  label: `${new Date().getFullYear()}.`,
                  value: new Date().getFullYear(),
                },
                {
                  label: `${new Date().getFullYear() - 1}.`,
                  value: new Date().getFullYear() - 1,
                },
                {
                  label: `${new Date().getFullYear() - 2}.`,
                  value: new Date().getFullYear() - 2,
                },
              ]}
              filter={false}
              labelText={t("Otkupna godina")}
              placeholderText={t("Izaberite iz liste")}
              control={control}
              setValue={setValue}
              errors={errors}
              rules={{}}
            />

            <RadioButtonInput
              id="transportCostRwa"
              value={form.transportCostRwa ?? false}
              radioButtonValue1={t("DA")}
              radioButtonValue2={t("NE")}
              onValueChange={handleFormChange}
              labelText={t("Trošak prevoza RWA")}
              control={control}
              setValue={setValue}
              errors={errors}
            />

            <TextAreaInput
              id="note"
              value={form.note ?? ""}
              onValueChange={handleFormChange}
              rows={5}
              cols={50}
              labelText={t("Napomena")}
              placeholderText={t("Unesite napomenu")}
              control={control}
              setValue={setValue}
              errors={errors}
            />

            <div className="approvals-container">
              {/* Ako nema approval-a u edit modu, prikaži prazno polje */}
              {form.approvals.length === 0 && form.id ? (
                <div className="approval-row">
                  <div className="approval-left">
                    <TextInput
                      id={`approvalNumber_0`}
                      value=""
                      onValueChange={(_, v) =>
                        handleApprovalChange(0, "number", v)
                      }
                      labelText={t("Broj aprobacionog uverenja") as string}
                      placeholderText={t("Unesite broj")}
                      control={control}
                      setValue={setValue}
                      errors={errors}
                      rules={{}}
                    />
                  </div>
                  <div className="approval-right">
                    <DateInput
                      id={`approvalDate_0`}
                      value={new Date()}
                      onValueChange={(_, v) =>
                        handleApprovalChange(0, "date", v)
                      }
                      labelText={t("Datum") as string}
                      placeholderText={t("Izaberite datum")}
                      icon="bi bi-calendar-check"
                      control={control}
                      errors={errors}
                    />
                    <ButtonInput
                      labelText={t("Dodaj broj") as string}
                      onButtonClick={addApprovalRow}
                      addStyle={{
                        background: "rgba(4, 102, 143, 1)",
                        border: "none",
                        marginLeft: "0.75rem",
                      }}
                    />
                  </div>
                </div>
              ) : (
                // Inače prikaži sve approval-e
                form.approvals.map((ap, index) => (
                  <div className="approval-row" key={index}>
                    <div className="approval-left">
                      <TextInput
                        id={`approvalNumber_${index}`}
                        value={ap.number}
                        onValueChange={(_, v) =>
                          handleApprovalChange(index, "number", v)
                        }
                        labelText={
                          index === 0
                            ? (t("Broj aprobacionog uverenja") as string)
                            : ""
                        }
                        placeholderText={t("Unesite broj")}
                        control={control}
                        setValue={setValue}
                        errors={errors}
                        rules={{}}
                      />
                    </div>

                    <div className="approval-right">
                      <DateInput
                        id={`approvalDate_${index}`}
                        value={ap?.date as Date}
                        onValueChange={(_, v) =>
                          handleApprovalChange(index, "date", v)
                        }
                        labelText={index === 0 ? (t("Datum") as string) : ""}
                        placeholderText={t("Izaberite datum")}
                        icon="bi bi-calendar-check"
                        control={control}
                        errors={errors}
                      />

                      {index === 0 ? (
                        <ButtonInput
                          labelText={t("Dodaj broj") as string}
                          onButtonClick={addApprovalRow}
                          addStyle={{
                            background: "rgba(4, 102, 143, 1)",
                            border: "none",
                            marginLeft: "0.75rem",
                          }}
                        />
                      ) : (
                        <ButtonInput
                          labelText=""
                          icon="bi bi-trash"
                          onButtonClick={() => removeApprovalRow(index)}
                          addStyle={{
                            background: "transparent",
                            border: "none",
                            color: "#dc3545",
                            marginLeft: "0.75rem",
                          }}
                        />
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>

            <div className="quality-row">
              <div className="quality-left">
                <RadioButtonInput
                  id="qualityOk"
                  value={form.qualityOk as boolean | undefined}
                  radioButtonValue1={t("DA")}
                  radioButtonValue2={t("NE")}
                  onValueChange={handleFormChange}
                  labelText={t("Roba odgovarajućeg kvaliteta")}
                  control={control}
                  setValue={setValue}
                  errors={errors}
                  disabled={
                    !hasApprovalData() ||
                    request?.hasOwnershipDocument === true ||
                    request?.badQualityTransferred === true
                  }
                />
              </div>

              {form.qualityOk === true && form.id && (
                <div className="quality-right">
                  <ButtonInput
                    labelText={t("Kreiraj vlasnički list") as string}
                    onButtonClick={handleCreateOwnershipDocument}
                    addStyle={{
                      background: "rgba(4, 102, 143, 1)",
                      border: "none",
                      whiteSpace: "nowrap",
                      cursor: "pointer",
                    }}
                  />
                  {showOwnershipSection && (
                    <div
                      style={{
                        marginTop: "1rem",
                        padding: "1rem",
                        border: "1px solid #dcdcdc",
                        borderRadius: "6px",
                        background: "#f8f9fa",
                      }}
                    >
                      <div
                        style={{
                          fontWeight: 600,
                          marginBottom: "0.75rem",
                        }}
                      >
                        {t("Kreiranje vlasničkog lista")}
                      </div>

                      <div style={{ marginBottom: "0.5rem" }}>
                        {t("Ukupna količina")}:{" "}
                        <strong>
                          {Utility.addThousandSeparator(ownershipTotalAmount)}
                        </strong>{" "}
                        kg
                      </div>

                      <div style={{ marginBottom: "0.5rem" }}>
                        {t("Već iskorišćeno")}:{" "}
                        <strong>
                          {Utility.addThousandSeparator(ownershipUsedAmount)}
                        </strong>{" "}
                        kg
                      </div>

                      <div style={{ marginBottom: "1rem" }}>
                        {t("Preostalo")}:{" "}
                        <strong>
                          {Utility.addThousandSeparator(
                            ownershipRemainingAmount,
                          )}
                        </strong>{" "}
                        kg
                      </div>

                      <TextInputWithIcon
                        id="ownershipAmount"
                        value={ownershipAmount ?? ""}
                        onValueChange={(_: string, value: any) => {
                          if (
                            value === "" ||
                            value === null ||
                            value === undefined
                          ) {
                            setOwnershipAmount(null);
                            return;
                          }

                          const parsed = Number(
                            String(value).replace(",", "."),
                          );

                          if (Number.isNaN(parsed)) {
                            return;
                          }

                          setOwnershipAmount(parsed);
                        }}
                        labelText={`${t("Količina za vlasnički list")} *`}
                        icon="bi bi-clipboard"
                        iconAlign="right"
                        placeholderText={t("Unesite količinu")}
                        control={control}
                        setValue={setValue}
                        errors={errors}
                        rules={{}}
                        disabled={ownershipRemainingAmount <= 0}
                      />

                      {ownershipRemainingAmount <= 0 && (
                        <small
                          style={{
                            color: "#dc3545",
                            display: "block",
                            marginTop: "0.5rem",
                          }}
                        >
                          {t(
                            "Celokupna količina je već iskorišćena za vlasničke listove.",
                          )}
                        </small>
                      )}

                      {ownershipAmount !== null &&
                        ownershipAmount > ownershipRemainingAmount && (
                          <small
                            style={{
                              color: "#dc3545",
                              display: "block",
                              marginTop: "0.5rem",
                            }}
                          >
                            {t("Uneta količina je veća od preostale količine.")}
                          </small>
                        )}

                      <div style={{ marginTop: "1rem" }}>
                        <ButtonInput
                          labelText={
                            t("Potvrdi kreiranje vlasničkog lista") as string
                          }
                          onButtonClick={handleConfirmCreateOwnershipDocument}
                          addStyle={{
                            background: "rgba(4, 102, 143, 1)",
                            border: "none",
                            opacity:
                              ownershipAmountInvalid ||
                              ownershipRemainingAmount <= 0
                                ? 0.6
                                : 1,
                            cursor:
                              ownershipAmountInvalid ||
                              ownershipRemainingAmount <= 0
                                ? "not-allowed"
                                : "pointer",
                          }}
                          disabled={
                            ownershipAmountInvalid ||
                            ownershipRemainingAmount <= 0
                          }
                        />
                      </div>
                    </div>
                  )}
                </div>
              )}

              {form.qualityOk === false && (
                <div className="quality-right quality-right-bad">
                  <div className="quality-right-bad-left">
                    <ButtonInput
                      labelText={t("Prebaci robu") as string}
                      onButtonClick={handleBadQualityTransfer}
                      addStyle={{
                        background: "rgba(4, 102, 143, 1)",
                        border: "none",
                        whiteSpace: "nowrap",
                        opacity: request?.badQualityTransferred ? 0.6 : 1,
                        cursor: request?.badQualityTransferred
                          ? "not-allowed"
                          : "pointer",
                      }}
                    />
                    {request?.badQualityTransferred && (
                      <small>
                        {t("Roba lošeg kvaliteta je već prebačena")}
                      </small>
                    )}
                  </div>

                  <div style={{ minWidth: "260px", flex: 1 }}>
                    <TextAreaInput
                      id="denaturationBasis"
                      value={form.denaturationBasis ?? ""}
                      onValueChange={handleFormChange}
                      labelText={t("Osnov denaturisanja") as string}
                      placeholderText={
                        t(
                          "Unesite osnov denaturisanja, npr. završenog agrotehničkog roka setve",
                        ) as string
                      }
                      control={control}
                      setValue={setValue}
                      errors={errors}
                      rules={{}}
                    />
                  </div>

                  <div className="quality-right-bad-right">
                    <ButtonInput
                      labelText=""
                      icon="bi bi-file-earmark-pdf"
                      onButtonClick={handleDenaturationRecord}
                      disabled={!request?.badQualityTransferred}
                      addStyle={{
                        background: "rgba(4, 102, 143, 1)",
                        border: "none",
                        minWidth: "40px",
                        width: "40px",
                        height: "40px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        opacity: !!request?.badQualityTransferred ? 1 : 0.6,
                        cursor: !!request?.badQualityTransferred
                          ? "pointer"
                          : "not-allowed",
                      }}
                    />
                    <small>{t("Zapisnik o denaturisanju")}</small>
                  </div>
                </div>
              )}
            </div>
            <div className="quality-row">
              {form.qualityOk === false && (
                <>
                  <DropdownInput
                    id="newProduct"
                    value={form.newProduct}
                    onValueChange={handleFormChange}
                    optionValue="id"
                    optionLabel="name"
                    options={productsList.filter(
                      (p: Product) =>
                        p.type === ProductType.MercantileGoods &&
                        p.isNatural !== true,
                    )}
                    filter={true}
                    labelText={t("Merkantilna roba")}
                    placeholderText={t("Izaberite iz liste")}
                    control={control}
                    setValue={setValue}
                    errors={errors}
                    rules={{}}
                  />

                  <DropdownInput
                    id="newWarehouse"
                    value={form.newWarehouse}
                    onValueChange={handleFormChange}
                    optionValue="id"
                    optionLabel="name"
                    options={
                      warehousesList.filter(
                        (w: Warehouse) =>
                          w.finishingCenter !== true && w.virtual !== true,
                      ) as Warehouse[]
                    }
                    filter={true}
                    labelText={t("Magacin")}
                    placeholderText={t("Izaberite iz liste")}
                    control={control}
                    setValue={setValue}
                    errors={errors}
                    rules={{}}
                  />
                </>
              )}
            </div>
          </div>
        </Dialog>
      )}
    </>
  );
};
